import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./StoreMegaMenu.css";
import axios from "axios";
import { resolveUrl, translateStoreCategory } from "../../../utils/storeUtils";

function safeId(v) {
  if (!v) return "";
  if (typeof v === "object" && v._id) return String(v._id);
  return String(v);
}

export default function StoreMegaMenu({ tree, isRtl, activeRootSlug, showRootsColumn = true }) {
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const apiBaseUrl = axios.defaults.baseURL || "";
  const labelFor = useCallback(
    (node) => {
      if (!node) return "";
      const en = String(node?.label || "").trim();
      const ar = String(node?.arLabel || "").trim();
      if (isRtl) return ar || translateStoreCategory(t, node) || en;
      return en || translateStoreCategory(t, node) || ar;
    },
    [isRtl, t]
  );
  const roots = useMemo(
    () => (Array.isArray(tree) ? tree.filter((c) => c?.isActive !== false) : []),
    [tree]
  );
  const [activeRootId, setActiveRootId] = useState(() => safeId(roots[0]?._id));
  const activeRoot = useMemo(
    () => roots.find((r) => safeId(r._id) === activeRootId) || roots[0] || null,
    [roots, activeRootId]
  );

  const setActiveRootBySlug = useCallback(
    (slug) => {
      const s = String(slug || "").trim().toLowerCase();
      if (!s) return;
      const found = roots.find((r) => String(r?.slug || "").trim().toLowerCase() === s);
      if (!found) return;
      const id = safeId(found._id);
      if (!id) return;
      setActiveRootId(id);
    },
    [roots]
  );

  React.useEffect(() => {
    if (!activeRootSlug) return;
    setActiveRootBySlug(activeRootSlug);
  }, [activeRootSlug, setActiveRootBySlug]);

  const secondLevel = useMemo(() => {
    const kids = Array.isArray(activeRoot?.children) ? activeRoot.children : [];
    return kids.filter((c) => c?.isActive !== false);
  }, [activeRoot]);
  const groups = useMemo(() => {
    return secondLevel.map((s) => {
      const children = (Array.isArray(s?.children) ? s.children : []).filter(
        (c) => c?.isActive !== false
      );
      return { section: s, items: children };
    });
  }, [secondLevel]);

  const visualImage = useMemo(() => {
    // Mega menu (store home) cover: prefer admin-controlled `storeHomeImageUrl`.
    // Fallback to `coverImageUrl`, then legacy `imageUrl`.
    const raw =
      String(activeRoot?.storeHomeImageUrl || "").trim() ||
      String(activeRoot?.coverImageUrl || "").trim() ||
      String(activeRoot?.imageUrl || "").trim();
    const resolved = raw ? resolveUrl(raw, apiBaseUrl) : "";
    return resolved;
  }, [activeRoot, apiBaseUrl]);

  const goTo = useCallback(
    (node) => {
      const slug = String(node?.slug || "").trim().toLowerCase();
      if (!slug) return;
      navigate(`/store/category/${slug}`);
    },
    [navigate]
  );

  return (
    <div
      className={`smm ${isRtl ? "is-rtl" : ""}${showRootsColumn ? "" : " smm--no-roots"}`}
      data-smm-root
    >
      {showRootsColumn ? (
        <div className="smm-col smm-col--roots" role="menu">
          {roots.map((r) => {
            const id = safeId(r._id);
            const active = id === safeId(activeRoot?._id);
            return (
              <button
                key={id}
                type="button"
                className={`smm-root${active ? " is-active" : ""}`}
                onMouseEnter={() => {
                  setActiveRootId(id);
                }}
                onFocus={() => {
                  setActiveRootId(id);
                }}
                onClick={() => goTo(r)}
              >
                <span className="smm-root-label">{labelFor(r)}</span>
                <i
                  className={`fa-solid ${isRtl ? "fa-chevron-left" : "fa-chevron-right"}`}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="smm-col smm-col--content" role="menu" aria-label={labelFor(activeRoot) || "Categories"}>
        <div className="smm-content-head">
          <div className="smm-col-title">{labelFor(activeRoot) || "Categories"}</div>
          {activeRoot ? (
            <button type="button" className="smm-viewall" onClick={() => goTo(activeRoot)}>
              {t("store.ourCategoriesViewAll", { defaultValue: "View all" })}
            </button>
          ) : null}
        </div>

        {groups.length === 0 ? (
          <div className="smm-empty">
            {t("store.megaMenuEmpty", { defaultValue: "No subcategories yet" })}
          </div>
        ) : (
          <div className="smm-columns">
            {groups.map(({ section, items }) => (
              <div key={safeId(section?._id)} className="smm-group">
                <button type="button" className="smm-group-title" onClick={() => goTo(section)}>
                  {labelFor(section)}
                </button>
                <div className="smm-group-list">
                  {items.slice(0, 8).map((leaf) => (
                    <button
                      key={safeId(leaf?._id)}
                      type="button"
                      className="smm-item"
                      onClick={() => goTo(leaf)}
                    >
                      {labelFor(leaf)}
                    </button>
                  ))}
                  {items.length > 8 ? (
                    <button type="button" className="smm-item smm-item--more" onClick={() => goTo(section)}>
                      + {items.length - 8}{" "}
                      {t("store.megaMenuMore", { defaultValue: "more" })}
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="smm-col smm-col--visual" aria-hidden="true">
        <div
          className="smm-visual"
          style={{
            backgroundImage: visualImage ? `url("${visualImage}")` : "none",
          }}
        >
          <div className="smm-visual-scrim" />
          <div className="smm-visual-caption">
            <div className="smm-visual-title">{labelFor(activeRoot)}</div>
            <div className="smm-visual-sub">
              {t("store.megaMenuTagline", { defaultValue: "Curated • Crafted • Timeless" })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

